import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FlaskConical, FileText, FilterX, ShieldCheck, ArrowRight } from 'lucide-react';

export default function Home() {
  return (
    <div className="space-y-12">
      <section className="relative overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
        <div className="subtle-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 p-6 sm:p-10 lg:p-14">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-3 py-1 text-[11px] tracking-wider uppercase text-[color:var(--muted-fg)]">
              <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--accent-teal)]" /> Citation-first research tool · Not medical advice
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Translate longevity research into <span className="text-[color:var(--accent-teal)]">biomarker-specific</span> protocols.
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-7 text-[color:var(--muted-fg)] max-w-2xl">
              The Healthspan Optimization Engine matches your biomarker profile to published interventions across the hallmarks of aging — mechanism by mechanism, evidence-tier by evidence-tier. Every recommendation links to a primary source.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/match"><Button className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Run a biomarker match <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/protocols"><Button variant="outline">Browse protocol catalogue</Button></Link>
            </div>
            <p className="mt-4 text-xs text-[color:var(--muted-fg)]">
              Educational use only · Designed for researchers, clinicians, and informed laypeople.
            </p>
          </div>
          <div className="hidden lg:block">
            <div className="relative h-full rounded-[var(--radius-md)] overflow-hidden border border-[color:var(--border-color)] bg-[color:var(--surface-2)]">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1650513248896-ae66deea01d3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div className="relative h-full p-6 flex flex-col justify-end">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--muted-fg)]">Hallmarks covered</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  {['Telomere attrition','Epigenetic drift','Mitochondrial dysfunction','Cellular senescence','Stem cell exhaustion','Chronic inflammation','Metabolic dysregulation','Proteostasis loss','Genomic instability'].map(h => (
                    <span key={h} className="rounded-[var(--radius-sm)] border border-[color:var(--border-color)] bg-[color:var(--surface)] px-2 py-1">{h}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4">
        {[{i:FlaskConical,t:'Enter biomarkers',d:'Telomere length, epigenetic age, CRP, IL-6, HbA1c, lipid panel and more — all optional, all unit-aware.'},{i:FileText,t:'Get ranked protocols',d:'Deterministic scoring across evidence tier, mechanism relevance, and biomarker fit. No black boxes.'},{i:FilterX,t:'Trace every claim',d:'Each protocol links to a primary source. Placeholders are flagged “Citations pending” — admin-replaceable.'}].map((s,idx)=>{
          const Ic = s.i;
          return (
            <div key={idx} className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] p-5">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] bg-[color:var(--surface-2)] text-[color:var(--accent-teal)] border border-[color:var(--border-color)]"><Ic className="h-4 w-4" /></div>
              <div className="mt-3 text-base font-semibold tracking-tight">{s.t}</div>
              <p className="mt-1 text-sm leading-6 text-[color:var(--muted-fg)]">{s.d}</p>
            </div>
          );
        })}
      </section>

      <section className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Ready to see protocols matched to your profile?</h2>
            <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Sign up free to save profiles. Upgrade for the Researcher tier (full technical summaries, references, CSV export).</p>
          </div>
          <div className="flex gap-2">
            <Link to="/register"><Button className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Create account</Button></Link>
            <Link to="/pricing"><Button variant="outline">View pricing</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
